/**
 * Название товара без приписок магазина.
 *
 * Живёт в общем пакете, потому что название чистят оба разбора: браузерный,
 * который работает на странице у человека, и серверный, который разбирает
 * присланную ссылку. Разные правила в этих двух местах означали бы, что одно
 * и то же желание выглядит по-разному в зависимости от того, как его добавили,
 * — и разъезжались бы они молча.
 *
 * Из корпуса настоящих страниц: «Сволочь. Том 4. Манхва 📖 купить в
 * Читай-городе (978-5-04-229372-6)» и «Книга: Мы вулканы. Купить книгу,
 * читать рецензии | Лабиринт». В вишлисте это выглядит как объявление,
 * а не как желание, и первым делом человек лезет всё это стирать руками.
 */

import { ЗНАКИ_ВАЛЮТ } from './money.js';
import { обычныеДефисы } from './hyphens.js';

/**
 * Слова, которыми магазины дополняют название ради поисковиков.
 *
 * Список не про конкретные магазины, а про приём: в заголовок дописывают то,
 * что человек мог бы искать. Ищем эти слова только в ХВОСТЕ после разделителя —
 * внутри самого названия «цена» встречается сплошь и рядом («Ценник для
 * витрины»), и вырезать по слову было бы дико.
 */
const ХВОСТ_МАГАЗИНА =
  /куп(ить|и|ите)|цена|цены|заказать|доставка|интернет-магазин|в наличии|отзывы|рецензии|характеристики|каталог|price|buy online/i;

/**
 * Приписка, приклеенная к названию БЕЗ разделителя.
 *
 * Правило по кускам ищет приписку только после « | », « — » или точки —
 * и молчит там, где магазин не поставил ничего. Из корпуса: у Иль де Ботэ
 * «…для ежедневной защиты лица купить в интернет магазине ИЛЬ ДЕ БОТЭ
 * с доставкой.», у Долины роз «…пикоти кафе 10шт из питомника Долина роз
 * с доставкой!». Разделителя нет ни там, ни там, и в вишлист приезжало
 * объявление целиком.
 *
 * Здесь поэтому не отдельные слова, а обороты в два слова и длиннее: «купить»
 * само по себе встречается внутри названий («Игра „Купи слона“»), а «купить в»,
 * «с доставкой» и «в интернет-магазине» — это уже речь магазина о себе.
 * Дефис в «интернет-магазине» необязателен: у Иль де Ботэ он написан
 * раздельно, и правило со строгим дефисом прошло бы мимо.
 *
 * Конец слова тут проверяется через `КОНЕЦ_СЛОВА`, а не через `\b`: в JavaScript
 * `\b` знает только латиницу, и «купить в» ему не конец слова вовсе — ни «ь»,
 * ни «в» для него не буквы. Правило с `\b` молча не срабатывало ни разу.
 */
const КОНЕЦ_СЛОВА = '(?![\\p{L}\\p{N}])';
const ХВОСТ_БЕЗ_РАЗДЕЛИТЕЛЯ = new RegExp(
  '\\s(?:' +
    `куп(?:ить|ите|и)\\s+(?:в|во|на|у|за|со?|по|онлайн|недорого|дешево|дёшево)${КОНЕЦ_СЛОВА}` +
    `|заказ(?:ать|ите)\\s+(?:в|во|на|с|со|по|онлайн)${КОНЕЦ_СЛОВА}` +
    `|с\\s+доставкой${КОНЕЦ_СЛОВА}` +
    '|в\\s+интернет[-\\s]?магазин\\p{L}*' +
    `|по\\s+(?:низкой\\s+)?цене${КОНЕЦ_СЛОВА}` +
    /*
     * «за 2 490 ₽» в конце названия — это ценник, а не имя вещи.
     *
     * Предлог здесь обязателен, и в нём вся разница. «Чайник за 2 490 ₽» —
     * магазин говорит, почём он его отдаёт. А «Подарочный сертификат 5000 ₽» —
     * это имя вещи, и срезать сумму значило бы отобрать у сертификата номинал.
     *
     * Без этого правила выходило хуже, чем без чистки вовсе: знак валюты
     * снимался вместе с прочей пунктуацией, и в вишлист приезжало
     * «Чайник стеклянный за 2 490» — оборванная фраза.
     */
    `|за\\s+[\\d\\s\\u00a0]+(?:[.,]\\d{1,2})?\\s*(?:${ЗНАКИ_ВАЛЮТ})` +
    ')',
  'iu',
);

/**
 * Приписка ПЕРЕД названием: призыв к покупке.
 *
 * Тот же приём, только с другого конца: «Купите лютик…» у Долины роз, «Купить
 * помещение в ДНТ…» на Циан, «Купить комплексные решения…» на Pult.ru. Вреда
 * от неё вдвое больше, чем от хвоста: она не только мусорит в вишлисте, но
 * и мешает узнать в заголовке страницы то же самое название — сравнение
 * начинается с чужого слова и обрывается на первой букве, а вместе с ним
 * пропадает и обрезка хвоста.
 *
 * Глаголы вынесены отдельно от зазывалок ниже, потому что решение по ним
 * разное. Названием вещи «Купить» не бывает никогда — даже если магазин сам
 * начал с него заголовок страницы, как Циан и Pult.ru. Значит и спрашивать
 * тут не о чем: снимаем всегда.
 */
const ПРИСТАВКА_ПРИЗЫВ = new RegExp(`^(?:куп(?:ить|ите|и)|заказ(?:ать|ите))${КОНЕЦ_СЛОВА}`, 'iu');

/**
 * Приписка ПЕРЕД названием: зазывалка.
 *
 * «Оригинал 💯 SPF 50+…» у Иль де Ботэ. В отличие от призыва, каждое из этих
 * слов бывает и названием: книга «Оригинал» существует, а на Tiu.ru продаётся
 * ровно «Акция Марафон бет „Приветственный бонус…“». Поэтому у зазывалки два
 * ограничения, которых нет у призыва: заголовок страницы имеет право вето,
 * и одно слово целым названием остаться не может.
 */
const ПРИСТАВКА_ЗАЗЫВАЛКА = new RegExp(
  `^(?:оригинал|новинка|распродажа|акция|скидк[аи]|хит\\s+продаж)${КОНЕЦ_СЛОВА}`,
  'iu',
);

/**
 * Мусор между приставкой и названием: пробелы, разделители и эмодзи.
 *
 * У Иль де Ботэ после «Оригинал» стоит «💯», и без этой чистки название
 * начиналось бы с эмодзи, а заголовок страницы — с буквы: сравнение бы
 * не сошлось. Открывающие кавычки и скобки сюда не входят намеренно —
 * они часть названия, и без них оно выглядит оборванным.
 */
const ВЕДУЩИЙ_МУСОР = /^(?:[\s|·–—\-‐‑‒−.,:;!]|\p{Extended_Pictographic}|️)+/u;

/**
 * Убирает приписку магазина, оставляя само название.
 *
 * Режем осторожно, по трём правилам сразу. Только по разделителю — внутрь
 * названия не лезем. Только если в хвосте есть слова магазина. И только если
 * от названия остаётся осмысленный кусок: лучше оставить лишнее, чем срезать
 * само желание.
 *
 * Довод `заголовокСтраницы` — это h1, если он есть. Магазины дописывают хвост
 * именно в конец, поэтому совпадение начала означает, что h1 — то же название,
 * только без приписки.
 */
export function очиститьНазваниеТовара(название: string, заголовокСтраницы?: string): string {
  let итог = название.replace(/\s+/g, ' ').trim();

  /*
   * Приставку снимаем ПЕРВОЙ — до сравнения с заголовком страницы.
   *
   * Порядок здесь не вкусовщина, а вся разница. У Долины роз заголовок
   * страницы — ровно название товара, но в разметке перед ним стоит «Купите»,
   * и сравнение обрывалось на первой же букве. Хвост «из питомника Долина роз
   * с доставкой!» оставался в желании из-за одного лишнего слова спереди.
   */
  const h1 = заголовокСтраницы?.replace(/\s+/g, ' ').trim();
  итог = безПриставкиМагазина(итог, h1);
  /*
   * Приставку снимаем и с ЗАГОЛОВКА тоже, иначе сравнение сходится не всегда.
   *
   * Замер 09.08.2026, Циан, живой корпус выкладки 50. В `og:title` — «Купить
   * помещение в ДНТ Реестр в поселке Воробьевский, продажа помещений. Найдено
   * 0 объявлений.», в h1 — «Купить помещение в ДНТ Реестр в поселке
   * Воробьевский». Заголовок отсекал хвост целиком, но сравнение до него не
   * доходило: с названия «Купить» уже снято, у заголовка оно ещё на месте, и
   * строки расходились на первой же букве. Человеку уезжало желание «продажа
   * помещений. Найдено 0 объявлений» в придачу к помещению.
   *
   * Снимаем той же мерой, что и с названия, — `безПриставкиМагазина(h1, h1)`:
   * призыв уходит всегда (названием вещи «Купить» не бывает), а зазывалка
   * остаётся, потому что вето заголовок даёт сам себе. Так «Акция Марафон
   * бет…» у Tiu.ru названием и остаётся.
   *
   * Чистим ТОЛЬКО для сравнения. Правила `названиеЭтоКусокЗаголовка` и
   * `названиеЗанимаетВесьЗаголовок` получают заголовок как есть, и это
   * намеренно: заголовок со словами магазина о себе именем товара не бывает,
   * и подчищать его перед этой проверкой значило бы протащить «Купить пончо
   * в Москве» в название вещи.
   */
  const h1ДляСравнения = h1 ? безПриставкиМагазина(h1, h1) : undefined;
  if (h1ДляСравнения && h1ДляСравнения.length >= 8) {
    /*
     * Сравниваем по буквам и цифрам, а знаки препинания пропускаем.
     *
     * Магазины пишут одно и то же название в заголовке и в разметке по-разному:
     * у Буквоеда в h1 «Фатум. Гаер», а в разметке «Фатум: Гаер книга Алисы
     * Джукич, Каролины Харит 📕 купить в Буквоеде». Сравнение строк как есть
     * спотыкалось на точке против двоеточия, и весь хвост оставался в названии
     * желания — вместе с эмодзи и именем магазина.
     *
     * Режем по исходной строке, а не подставляем h1: в нём знаки могут быть
     * беднее («Фатум. Гаер» вместо «Фатум: Гаер»), а название товара человек
     * привык видеть таким, как на витрине.
     */
    const конец = концаЗаголовкаВ(итог, h1ДляСравнения);
    if (конец !== undefined && конец < итог.length) {
      const обрезанное = итог.slice(0, конец).replace(/[\s|·–—.,:;-]+$/, '').trim();
      if (обрезанное.length >= 3) итог = обрезанное;
    }
  }

  /*
   * Ищем, с какого куска начинается приписка, а не рубим по последнему
   * разделителю.
   *
   * Разница видна на Лабиринте: «Книга: Мы вулканы - Наталья Преображенская.
   * Купить книгу, читать рецензии | Лабиринт». Последний разделитель отрезает
   * только « | Лабиринт» — слов магазина в нём нет, и на этом всё встаёт.
   * А приписка начинается раньше, с «Купить книгу».
   *
   * Поэтому идём слева направо и режем на первом куске, который сам по себе
   * похож на приписку. Так «Ноутбук Acer. 16ГБ DDR5. Купить в Ситилинке»
   * теряет только последний кусок, а не половину описания.
   */
  /*
   * Ищем по строке с обычными дефисами, а режем по исходной.
   *
   * Замена посимвольная, один знак на один, поэтому место совпадения в обеих
   * строках одно и то же — а название человек видит таким, каким его написал
   * магазин. Без этого «Куртка — в интернет‑магазине X» оставалась целиком:
   * приписку с неразрывным дефисом правило не узнавало.
   */
  const куски = разбитьНаКуски(итог);
  const приписка = куски.findIndex(
    (кусок, номер) => номер > 0 && ХВОСТ_МАГАЗИНА.test(обычныеДефисы(кусок.текст)),
  );
  const началоПриписки = приписка > 0 ? куски[приписка]?.начало : undefined;
  if (началоПриписки !== undefined) {
    const остаток = итог.slice(0, началоПриписки).replace(/[\s|·–—.-]+$/, '').trim();
    // Название целиком состоит из приписки — значит это не приписка, а само
    // название («Купить подарочный сертификат»). Оставляем как есть.
    if (остаток.length >= 10) итог = остаток;
  }

  /*
   * Последней — приписка, приклеенная без разделителя.
   *
   * Последней, потому что это самое грубое из правил: остальные опираются
   * на то, что магазин сам отделил приписку или сам назвал товар в заголовке.
   * Здесь же мы режем посреди фразы, доверяя только обороту. Пока предыдущие
   * правила справляются, до этого дело не доходит.
   */
  /*
   * Идём по всем совпадениям, а не по первому.
   *
   * Первое может оказаться слишком близко к началу — тогда от названия
   * останется огрызок, и мы такое не режем. Но раньше на этом поиск и
   * заканчивался, а следом в строке могла стоять честная приписка: у «Набор
   * за 1 200 ₽ с доставкой» первое совпадение стоит на шестом знаке и
   * отвергается, и «с доставкой» из-за этого оставалось в желании.
   */
  const поиск = new RegExp(ХВОСТ_БЕЗ_РАЗДЕЛИТЕЛЯ.source, 'giu');
  const дляПоиска = обычныеДефисы(итог);
  for (let найдено = поиск.exec(дляПоиска); найдено !== null; найдено = поиск.exec(дляПоиска)) {
    if (найдено.index < 10) continue;
    итог = итог.slice(0, найдено.index).replace(/[\s|·–—.,-]+$/, '').trim();
    break;
  }

  // Приставка могла и открыться заново: «Ноутбук Acer — купите в Ситилинке»
  // после обрезки хвоста превращается в «Ноутбук Acer», а вот у «Купите
  // ноутбук. Купить в Ситилинке» первое слово вылезает только сейчас.
  итог = безПриставкиМагазина(итог, h1);

  return итог.replace(/[\s|·–—-]+$/, '').trim();
}

/**
 * Снимает рекламные слова и мусор перед названием.
 *
 * Повторяет проход, пока есть что снимать: «Купите оригинал 💯 …» — это две
 * приставки подряд, и одного прохода мало. Ничего не трогает, если после
 * снятия остаётся меньше десяти значимых знаков: «Оригинал» и «Скидка» бывают
 * и названиями, а превратить желание в пустоту хуже, чем оставить лишнее.
 *
 * И главное: у зазывалки заголовок страницы имеет право вето. Если сам магазин
 * начинает заголовок с того же слова — это не приписка, а название. Из корпуса:
 * на Tiu.ru и в разметке, и в заголовке стоит «Акция Марафон бет
 * „Приветственный бонус…“», и слово «Акция» здесь ровно то, что продаётся.
 *
 * На призыв вето не распространяется, и это не недосмотр: у Циан заголовок
 * страницы — «Купить помещение в ДНТ Реестр», и вето превратило бы желание
 * в «Купить помещение…», хотя человек хочет помещение, а не покупку.
 */
function безПриставкиМагазина(название: string, заголовокСтраницы?: string): string {
  const зазывалкаВЗаголовке = Boolean(заголовокСтраницы && ПРИСТАВКА_ЗАЗЫВАЛКА.test(заголовокСтраницы));

  let итог = название;
  for (let проход = 0; проход < 4; проход += 1) {
    const очищенный = итог.replace(ВЕДУЩИЙ_МУСОР, '');
    const безПризыва = очищенный.replace(ПРИСТАВКА_ПРИЗЫВ, '');
    const безЗазывалки = зазывалкаВЗаголовке ? безПризыва : безПризыва.replace(ПРИСТАВКА_ЗАЗЫВАЛКА, '');
    const короче = безЗазывалки.replace(ВЕДУЩИЙ_МУСОР, '');
    if (короче === итог) return итог;
    if (короче.replace(/[^\p{L}\p{N}]/gu, '').length < 10) return итог;
    итог = короче;
  }
  return итог;
}

/**
 * Где в названии кончается то, что написано в заголовке страницы.
 *
 * Идём по обеим строкам, пропуская всё, что не буква и не цифра: так «Фатум.
 * Гаер» и «Фатум: Гаер книга...» совпадают ровно до слова «книга». Возвращает
 * место в исходной строке — или ничего, если заголовок ей не начало.
 */
function концаЗаголовкаВ(текст: string, заголовок: string): number | undefined {
  const значимый = (символ: string | undefined): boolean => Boolean(символ) && /[\p{L}\p{N}]/u.test(символ as string);

  let вТексте = 0;
  let вЗаголовке = 0;
  while (вЗаголовке < заголовок.length) {
    if (!значимый(заголовок[вЗаголовке])) {
      вЗаголовке += 1;
      continue;
    }
    while (вТексте < текст.length && !значимый(текст[вТексте])) вТексте += 1;
    if (вТексте >= текст.length) return undefined;
    if (текст[вТексте]?.toLowerCase() !== заголовок[вЗаголовке]?.toLowerCase()) return undefined;
    вТексте += 1;
    вЗаголовке += 1;
  }

  /*
   * Закрывающие скобки и кавычки забираем с собой.
   *
   * Последняя значимая буква названия «Акустические гитары Taylor (б/у)» — это
   * «у», и обрез сразу после неё оставлял висящую скобку: «Taylor (б/у».
   * Скобка закрывает то, что уже вошло в название, и без неё название выглядит
   * оборванным.
   */
  while (вТексте < текст.length && /[)\]}»”"'›]/.test(текст[вТексте] ?? '')) вТексте += 1;
  return вТексте;
}

/** Разбивает название на куски по разделителям, запоминая, где какой начался. */
function разбитьНаКуски(текст: string): { текст: string; начало: number }[] {
  const куски: { текст: string; начало: number }[] = [];
  const разделитель = /\s\|\s|\s—\s|\s–\s|\.\s/g;
  let предыдущий = 0;
  for (const совпадение of текст.matchAll(разделитель)) {
    куски.push({ текст: текст.slice(предыдущий, совпадение.index), начало: предыдущий });
    предыдущий = совпадение.index + совпадение[0].length;
  }
  куски.push({ текст: текст.slice(предыдущий), начало: предыдущий });
  return куски;
}

/**
 * Название, которым страница объявляет себя служебной, а не товаром.
 *
 * Из корпуса: у Фотосклада раздел зеркальных фотоаппаратов называется «Главная
 * страница», у Baon по битой ссылке открывается «Baon: Интернет-магазин».
 * Обе страницы разбор уверенно признавал карточкой товара — с ценой,
 * с фотографией, со всем, — и человек получал желание «Главная страница»
 * по цене случайного фотоаппарата из сетки.
 *
 * Список нарочно короткий и точный: это не «похоже на служебную», а «магазин
 * прямо так её и назвал». Ни одно настоящее название товара сюда не попадает,
 * поэтому правилом можно пользоваться как окончательным.
 */
const СЛУЖЕБНОЕ_НАЗВАНИЕ: RegExp[] = [
  /^главная(\s+страница)?$/i,
  /^домашняя\s+страница$/i,
  /^каталог(\s+товаров)?$/i,
  /^корзина$/i,
  /^избранное$/i,
  /^результаты\s+поиска$/i,
  // Конец слова выписан руками: `\b` в JavaScript знает только латиницу,
  // и с «магазин» перед пробелом он не срабатывает вовсе.
  /^интернет[-\s]?магазин(?![\p{L}\p{N}])/iu,
  /[:—|]\s*интернет[-\s]?магазин\.?$/i,
];

/**
 * Потолок длины заголовка, при котором он ещё может БЫТЬ названием вещи.
 *
 * Названия длиннее двухсот знаков всё равно обрезаются обоими разборами, так
 * что заголовок длиннее — это уже не имя товара, а блок вёрстки, который
 * магазин зачем-то обернул в h1. Тянуть из такого нечего.
 */
const ЗАГОЛОВОК_ЕЩЁ_НАЗВАНИЕ = 200;

/**
 * Магазин разметил именем товара лишь КУСОК собственного заголовка.
 *
 * Замер 08.08.2026, Л’Этуаль: `itemprop="name"` стоит на «ДЕЗОДОРАНТ-СПРЕЙ»,
 * а бренд и линейка — на соседних блоках внутри того же h1
 * («CHANEL GABRIELLE CHANEL ДЕЗОДОРАНТ-СПРЕЙ»). Человек читает на странице
 * название с брендом, а в вишлист уезжал один тип средства: «ДЕЗОДОРАНТ-СПРЕЙ»
 * без единого слова о том, чей он и какой. По такому желанию вещь не найти.
 *
 * Разметке мы верим больше вёрстки, и это правило её не отменяет: заголовок
 * берётся только там, где размеченный узел ЛЕЖИТ ВНУТРИ h1. Вложенность
 * объявил сам магазин, догадки тут нет — он сказал, что имя вещи находится
 * вот в этом заголовке, и не сказал, что заголовок именем не является.
 *
 * Три ограничения, и каждое поставлено по корпусу, а не из осторожности
 * вообще. Из 106 настоящих страниц узел имени лежит внутри h1 ровно у двух.
 *
 * Первое: заголовок должен содержать размеченное имя. Вложенность без
 * совпадения текста означает, что имя чем-то скрыто или переписано скриптом,
 * и тогда мы про заголовок ничего не знаем.
 *
 * Второе: в заголовке не должно быть слов магазина о себе. Вторая из двух
 * страниц — Альпиндустрия, и она показывает, зачем: там в h1 лежат крошки
 * «Каталог / Одежда / Пончо», а `itemprop="name"` стоит на «Каталог».
 * Правило без этой проверки сменило бы честное «Пончо» на дорожку крошек.
 * Тем же отсекается «Купить X в Москве»: заголовок-зазывалка названием не
 * бывает, и размеченный кусок в нём как раз честнее целого.
 *
 * Третье — длина, см. `ЗАГОЛОВОК_ЕЩЁ_НАЗВАНИЕ`.
 *
 * Чего правило не ловит: заголовок, в котором к имени приписано что-то своё
 * без единого слова магазина — рейтинг, артикул, «новинка». Такой страницы
 * в корпусе нет ни одной, и выдумывать под неё правило значило бы проверять
 * собственную фантазию. Появится — будет видно по описи корпуса.
 */
export function названиеЭтоКусокЗаголовка(название: string, заголовок: string | undefined): boolean {
  const имя = (название ?? '').replace(/\s+/g, ' ').trim();
  const h1 = (заголовок ?? '').replace(/\s+/g, ' ').trim();

  if (!имя || !h1) return false;
  if (h1.length > ЗАГОЛОВОК_ЕЩЁ_НАЗВАНИЕ) return false;
  // Заголовок обязан быть ШИРЕ имени: равный ничего не добавляет.
  if (h1.length <= имя.length) return false;
  if (!h1.toLowerCase().includes(имя.toLowerCase())) return false;
  if (ХВОСТ_МАГАЗИНА.test(h1)) return false;

  return true;
}

/**
 * Магазин разметил именем товара ВЕСЬ свой заголовок, слово в слово.
 *
 * Пара к правилу выше, и нужна она не для красоты. Там имя — кусок заголовка,
 * и заголовок берётся вместо него; здесь брать нечего, они совпали. Значение
 * у этого случая одно — ОЦЕНКА, и прежде она выходила наизнанку: чем полнее
 * магазин подтвердил имя, тем меньше мы ему верили.
 *
 * Замер 09.08.2026, Бронницкий ювелир, живой корпус выкладки 49. В области
 * `schema.org/Product` стоит `<h1 itemprop="name">Кольцо из белого золота
 * с бриллиантами</h1>` — магазин двумя способами сразу сказал, как вещь
 * называется. Микроразметка отдавала это имя с оценкой 0,75, `og:title` —
 * «Кольцо обручальное RD000585Я105 цены, купить в Москве» с оценкой 0,8,
 * и в вишлист человеку уезжало «Кольцо обручальное RD000585Я105 цены».
 * Слово «цены» в названии желания — само по себе улика: чистка хвоста
 * сняла «купить в Москве» и не сняла остаток, потому что снимать речь
 * магазина из строки, написанной для поисковика, нечем до конца.
 *
 * Довод тот же, что у `названиеЭтоКусокЗаголовка`: заголовок написан для
 * человека, и чистить в нём нечего. Совпадение с размеченным именем — довод
 * СИЛЬНЕЕ вложенности, а не слабее: там мы достраиваем имя догадкой о том,
 * что соседние блоки заголовка тоже про вещь, здесь догадки нет вовсе.
 * Поэтому и оценка та же — 0,85, выше `og:title`.
 *
 * Ограничители — те же три и по той же причине, включая слова магазина
 * о себе: заголовок «Купить пончо в Москве», размеченный целиком, именем
 * не становится.
 */
export function названиеЗанимаетВесьЗаголовок(название: string, заголовок: string | undefined): boolean {
  const имя = (название ?? '').replace(/\s+/g, ' ').trim();
  const h1 = (заголовок ?? '').replace(/\s+/g, ' ').trim();

  if (!имя || !h1) return false;
  if (h1.length > ЗАГОЛОВОК_ЕЩЁ_НАЗВАНИЕ) return false;
  if (имя.toLowerCase() !== h1.toLowerCase()) return false;
  if (ХВОСТ_МАГАЗИНА.test(h1)) return false;

  return true;
}

/**
 * В имени страницы — речь магазина о себе, а не имя вещи.
 *
 * Живой прогон корпуса разделов 09.08.2026: из двадцати семи ссылок на
 * не-товар конвейер промолчал на одной — раздел зонтов ZENDEN. Адрес
 * `/catalog/accessories/umbrellas/` номера не несёт, но раздел размечен
 * ровно одним `Product`, и связка «нетоварный адрес И разметка не про одну
 * вещь» не сработала. Человеку уезжало желание «зонт в Москве недорого -
 * интернет-магазин ZENDEN» за 399 ₽ — цена случайного зонта из сетки, и ни
 * слова о том, что это раздел. Прежде эту страницу ловил счёт названий:
 * магазин переставил разметку, и примета исчезла.
 *
 * Само по себе это правило приметой быть не может, и замер говорит почему:
 * в описи из 106 карточек слова магазина в имени стоят у двух настоящих
 * товаров — «Любовь и ярость (Сутер Мартин) - купить книгу» у book24 и
 * «Консервы… (цена за упаковку)» у zoomag. Второе — законная часть имени,
 * и оболгать её предупреждением нельзя.
 *
 * Поэтому примета работает ТОЛЬКО в паре с нетоварным адресом — так же, как
 * стоят в паре прочие приметы витрины. Замер пары: ни одной из 106 карточек
 * описи и ни одной из 80 живых она не задевает, а раздел ZENDEN ловит.
 */
export function имяГоворитОМагазине(название: string | null | undefined): boolean {
  const текст = обычныеДефисы(название ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!текст) return false;
  return ХВОСТ_МАГАЗИНА.test(текст);
}

export function названиеСлужебнойСтраницы(название: string | null | undefined): boolean {
  // Дефис приводим к обычному: Wildberries пишет «Интернет‑магазин» неразрывным
  // (U+2011), и правило со строгим `-` проходило мимо самой частой вывески.
  const текст = обычныеДефисы(название ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!текст) return false;
  return СЛУЖЕБНОЕ_НАЗВАНИЕ.some((правило) => правило.test(текст));
}
